import {
  IAllSequestrationRulesetsResponse,
  ISequestrationRulesetCreateManyRequest,
  ISequestrationRulesetCreateRequest,
  ISequestrationRulesetResponse,
} from "types/SequestrationRuleset/SequestrationRuleset";

import { api } from ".";

/**
 * CLE-2325 — Sequester ruleset CRUD. Backend resolver lives in
 * services/clear-api/src/domains/sequestration_rulesets; UI surface is the standard
 * payer-rules editor (list, single create, multi-select fan-out create, edit, archive).
 */
const sequestrationRulesetQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllSequestrationRulesets: builder.query<
      IAllSequestrationRulesetsResponse,
      { isDefault?: boolean; includeArchived?: boolean } | null | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params && params.isDefault !== undefined)
          search.set("isDefault", String(params.isDefault));
        if (params && params.includeArchived === true)
          search.set("includeArchived", "true");
        const qs = search.toString();
        return `sequestration-rulesets/all${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["SequestrationRulesets"],
    }),
    getSequestrationRuleset: builder.query<
      ISequestrationRulesetResponse,
      string
    >({
      query: (id) => `sequestration-rulesets/${id}`,
      providesTags: (_r, _e, id) => [{ type: "SequestrationRulesets", id }],
    }),
    createSequestrationRuleset: builder.mutation<
      ISequestrationRulesetResponse,
      ISequestrationRulesetCreateRequest
    >({
      query: (body) => ({
        url: "sequestration-rulesets",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SequestrationRulesets"],
    }),
    /**
     * Multi-select fan-out — N×M rows in one transaction. Used by the create form when
     * the user selects multiple providers and/or payers.
     */
    createManySequestrationRulesets: builder.mutation<
      IAllSequestrationRulesetsResponse,
      ISequestrationRulesetCreateManyRequest
    >({
      query: (body) => ({
        url: "sequestration-rulesets/many",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SequestrationRulesets"],
    }),
    updateSequestrationRuleset: builder.mutation<
      ISequestrationRulesetResponse,
      { id: string; body: ISequestrationRulesetCreateRequest }
    >({
      query: ({ id, body }) => ({
        url: `sequestration-rulesets/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["SequestrationRulesets"],
    }),
    deleteSequestrationRuleset: builder.mutation<void, string>({
      query: (id) => ({
        url: `sequestration-rulesets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SequestrationRulesets"],
    }),
  }),
});

export const {
  useGetAllSequestrationRulesetsQuery,
  useGetSequestrationRulesetQuery,
  useCreateSequestrationRulesetMutation,
  useCreateManySequestrationRulesetsMutation,
  useUpdateSequestrationRulesetMutation,
  useDeleteSequestrationRulesetMutation,
} = sequestrationRulesetQueries;
